import { CancelablePromise as Promise } from "./cancelablePromise";
import DriveApi from "./api";
import * as translatorTools from "translator-tools";
import * as logger from "./logger";
import { TranslationSource, UntranslatedLogging } from "../utils/helpers";
import { DriveStore } from "../store/reducers";
import { TranslationActions } from "../store/translation/actions";
import { _originalLocalStorage } from "./storage/storage.base";
import { DRIVE_STORAGE_PREFIX } from "../store/storageConstants";

interface ITranslationDictionary {
    [index: string]: string;
}

// Should be refactored into a static class!
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class TranslationStorage {

    static dictionary: ITranslationDictionary | null = null;
    private static readonly dictionaryLanguage = "";
    private static readonly localStoragePrefix = `${ DRIVE_STORAGE_PREFIX }@translation_`;

    static cleanUp(exceptLanguage?: string): void {
        let keys = Object.keys(_originalLocalStorage);
        let exceptLanguageKey = this.getStorageKey(exceptLanguage);

        keys.forEach((key) => {
            if (key.indexOf(this.localStoragePrefix) === 0 && key !== exceptLanguageKey) {
                _originalLocalStorage.removeItem(key);
            }
        });
    }

    static get(language: string, key: string): string | null {
        let storage = this.getStorage(language);
        return storage[key] ? storage[key] : null;
    }

    static set(language: string, key: string, value: string) {
        let storage = this.getStorage(language);
        storage[key] = value;
        this.setStorage(language, storage);
    }

    static clearLegacyLocalStorage(language: string): void {
        let langKey = this.getStorageKey(language);
        _originalLocalStorage.removeItem(langKey);
    }

    private static getStorageKey(language?: string) {
        return `${ this.localStoragePrefix }${ language || "" }`;
    }

    private static getStorage(language: string): ITranslationDictionary {
        if (this.dictionary !== null && this.dictionaryLanguage === language) {
            return this.dictionary;
        } else {
            let dictString = _originalLocalStorage.getItem(this.getStorageKey(language));
            if (dictString) {
                try {
                    let dict = (<ITranslationDictionary> JSON.parse(dictString));
                    this.dictionary = dict || {};
                    return this.dictionary;
                } catch (e) {
                    return {};
                }
            } else {
                return {};
            }
        }
    }

    private static setStorage(language: string, storage: ITranslationDictionary) {
        _originalLocalStorage.setItem(this.getStorageKey(language), JSON.stringify(storage));
    }
}

// Should be refactored into a static class!
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class TranslatorStorage {
    static untranslatedStrings: Set<string> = new Set<string>();

    static translateSentences(api: DriveApi, sentences: string[], language: string, store: DriveStore): Promise<string[]> {
        let translated: string[] = [];
        let toLoadTranslationsDict = {};
        let toLoadTranslations;

        (sentences || []).forEach((sentence, index) => {
            if (sentence.length === 0) {
                // empty string does not require translation, continue
                translated[index] = sentence;
                return;
            }

            let translatedSentence = TranslationStorage.get(language, sentence);
            if (translatedSentence !== null) {
                translated[index] = translatedSentence;
            } else {
                toLoadTranslationsDict[sentence] = true;
            }
        });

        toLoadTranslations = Object.keys(toLoadTranslationsDict);

        if (toLoadTranslations.length > 0) {
            return new Promise<string[]>((resolve, reject) => {
                api.call("Translate", {
                    sentences: toLoadTranslations,
                    language: language
                }, result => {
                    (result || []).forEach((translatedString, index) => {
                        this.logIfUntranslated(store, translatedString);
                        TranslationStorage.set(language, toLoadTranslations[index], translatorTools.decodeSymbols(translatedString));
                    });
                    (sentences || []).forEach((sentence, index) => {
                        if (!translated[index]) {
                            translated[index] = TranslationStorage.get(language, sentence) || (`**${ sentence }`);
                        }
                    });
                    resolve(translated);
                }, reject, true);
            });
        } else {
            return Promise.resolve(translated);
        }
    }

    static translateSentencesRedux(api: DriveApi, sentences: string[], language: string, store: DriveStore): Promise<string[]> {
        let translated: string[] = [];
        let toLoadTranslations: string[] = [];
        let currentTranslations: DeepReadonlyObject<{[key: string]: string}>;

        if (sentences.length > 0){
            currentTranslations = store.getState().translations?.sentenceDirectory;
        }
        (sentences || []).forEach((sentence, index) => {
            if (sentence.length === 0) {
                // empty string does not require translation, continue
                translated[index] = sentence;
                return;
            }

            let translatorStore: string | undefined = currentTranslations[sentence];
            if (translatorStore !== undefined) {
                translated[index] = translatorStore;
            } else {
                toLoadTranslations.push(sentence);
            }
        });

        if (toLoadTranslations.length > 0) {
            return new Promise<string[]>((resolve, reject) => {
                api.call("Translate", {
                    sentences: toLoadTranslations,
                    language: language
                }, result => {
                    let sentenceDirectory: ITranslationDictionary = {};
                    if (result) {
                        result.forEach((translatedString: string, index: number) => {
                            this.logIfUntranslated(store, translatedString);
                            sentenceDirectory[toLoadTranslations[index]] = translatedString;
                        });

                        // Ignore stale responses so they cannot merge into the active language dictionary.
                        if (store.getState().translations.language === language) {
                            store.dispatch(TranslationActions.setTranslations({ language: language, sentenceDirectory: sentenceDirectory }));
                        }
                    }

                    let activeTranslations = store.getState().translations;
                    (sentences || []).forEach((sentence: string, index: number) => {
                        if (!translated[index]) {
                            let cachedTranslation = activeTranslations.language === language ? activeTranslations.sentenceDirectory[sentence] : undefined;
                            translated[index] = sentenceDirectory[sentence] || cachedTranslation || (`**${ sentence }`);
                        }
                    });
                    resolve(translated);
                }, reject, true);
            });
        } else {
            return Promise.resolve(translated);
        }
    }

    static clearLegacyLocalStorage(exceptLanguage: string): void {
        TranslationStorage.clearLegacyLocalStorage(exceptLanguage);
    }

    static cleanUp(exceptLanguage: string, store: DriveStore): void {
        TranslationStorage.cleanUp(exceptLanguage);
        // Check if store is there and has translations, if so clear it, not needed at the moment
        if (Object.keys(store.getState()?.translations.sentenceDirectory).length > 0) {
            store.dispatch(TranslationActions.clearTranslations({}));
        }
    }

    static cleanUpRedux(language: string, store: DriveStore): void {
        // Need to ensure we don't have both the regular local storage and the redux store
        TranslationStorage.clearLegacyLocalStorage(language);
        store.dispatch(TranslationActions.clearTranslations({ language: language }));
    }

    static get(text: string, language: string) {
        return TranslationStorage.get(language, text);
    }

    private static logIfUntranslated(store, translatedString){
        if (translatedString.includes("**") && !this.untranslatedStrings.has(translatedString)){
            let storeState = store.getState();
            let string: UntranslatedLogging = {
                untranslatedString: translatedString.trim().substring(2),
                page: storeState.mobile.currentPage,
                pageState: storeState.mobile.pageState,
                version: storeState.data.clientVersion,
                source: TranslationSource.API
            };
            logger.log(logger.Source.UNTRANSLATED_STRING, `Untranslated String: ${ JSON.stringify(string) }`);
            this.untranslatedStrings.add(translatedString);
        }
    }
}